import React, { useState, useEffect, useMemo } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import AddCommentModal from "./AddCommentModal";
import { usePageConfig } from "../hooks/usePageConfig";
import { buildMediaUrl } from "../utils/mediaUrl";

const EditPartModal = ({
  isOpen,
  onClose,
  partData,
  assessmentData,
  onRefreshAssessment,
}) => {
  const { clientConfig } = usePageConfig();
  const [isAnimating, setIsAnimating] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedAction, setSelectedAction] = useState("");
  const [hasChanges, setHasChanges] = useState(false);
  const [showCommentModal, setShowCommentModal] = useState(false);

  // Helper function to get the initial action from part data
  const getInitialAction = (part) => {
    if (!part) return "No Damage";

    // Check various possible fields for action/status
    if (part.Action) return part.Action;
    if (part.action) return part.action;
    if (part.suggested_action) return part.suggested_action;
    if (part.status) return part.status;
    if (part.repair_status) return part.repair_status;

    // Check in damage_images array
    if (part.damage_images && part.damage_images.length > 0) {
      for (const img of part.damage_images) {
        if (img.Action) return img.Action;
        if (img.action) return img.action;
        if (img.suggested_action) return img.suggested_action;
        if (img.status) return img.status;
        if (img.repair_status) return img.repair_status;
      }
    }

    return "No Damage"; // Default to No Damage if no action found
  };

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      setCurrentImageIndex(0); // Reset to first image when modal opens
      const initialAction = getInitialAction(partData);
      setSelectedAction(initialAction);
      setHasChanges(false); // Reset changes when modal opens
    }
  }, [isOpen, partData]);

  const handleActionChange = (action) => {
    const initialAction = getInitialAction(partData);
    setSelectedAction(action);
    setHasChanges(action !== initialAction);
  };

  const handleSaveChanges = () => {
    if (hasChanges) {
      setShowCommentModal(true);
    }
  };

  const handleCommentSubmit = (comment) => {
    // Here you would typically make an API call to save the changes and comment

    // Close the comment modal and the edit modal
    setShowCommentModal(false);
    setHasChanges(false);
    handleClose();

    // Refresh the assessment data to show updated damages
    if (onRefreshAssessment) {
      onRefreshAssessment();
    }
  };

  const handleCommentModalClose = () => {
    setShowCommentModal(false);
  };

  // Helper function to get a valid image source
  const getImageSource = (image) => {
    if (!image) return null;
    const imagePath = image.upload_picture || image.picture;
    if (!imagePath || imagePath.trim() === "") return null;
    return buildMediaUrl(imagePath, {
      baseUrl: clientConfig.mediaBaseUrl,
      pathPrefix: clientConfig.mediaPathPrefix,
    });
  };

  // Combine all images (part images + damage images) - do this before early return
  // Filter out invalid images that would appear blank
  const allImages = useMemo(() => {
    if (!partData) return [];

    return [
      ...(partData.part_images || []).filter(
        (img) =>
          img &&
          (img.upload_picture || img.picture) &&
          (img.upload_picture || img.picture).trim() !== ""
      ),
      ...(partData.damage_images || []).filter(
        (img) =>
          img &&
          (img.upload_picture || img.picture) &&
          (img.upload_picture || img.picture).trim() !== ""
      ),
    ];
  }, [partData]);

  // Debug logging to understand image availability
  useEffect(() => {
    if (partData) {
    }
  }, [partData, allImages]);

  // Ensure currentImageIndex is valid whenever allImages changes
  useEffect(() => {
    if (allImages.length > 0 && currentImageIndex >= allImages.length) {
      setCurrentImageIndex(0);
    } else if (allImages.length === 0) {
      setCurrentImageIndex(0);
    }
  }, [allImages.length, currentImageIndex]);

  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(() => {
      onClose();
    }, 300); // Wait for animation to complete
  };

  if (!isOpen || !partData) return null;

  const goToPreviousImage = () => {
    if (allImages.length === 0) return;
    setCurrentImageIndex((prev) => {
      const newIndex = prev === 0 ? allImages.length - 1 : prev - 1;
      return Math.max(0, Math.min(newIndex, allImages.length - 1));
    });
  };

  const goToNextImage = () => {
    if (allImages.length === 0) return;
    setCurrentImageIndex((prev) => {
      const newIndex = prev === allImages.length - 1 ? 0 : prev + 1;
      return Math.max(0, Math.min(newIndex, allImages.length - 1));
    });
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end justify-center"
      onClick={handleClose}
    >
      <div
        className={`bg-background-secondary border-t border-border-secondary rounded-tl-[24px] rounded-tr-[24px] w-full h-[85vh] max-h-[85vh] lg:w-[80%] lg:h-[65vh] relative overflow-hidden flex flex-col transition-transform duration-300 ease-out ${
          isAnimating ? "translate-y-0" : "translate-y-full"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="box-border flex items-center justify-between px-4 py-[12px] border-b border-border-secondary flex-shrink-0">
          <div className="flex items-center gap-4">
            <h2 className="text-xl font-semibold text-text-primary">
              Edit damge on Part: {partData.part_name || "Unknown Part"}
            </h2>
          </div>

          {/* Close Button */}
          <button
            className="block cursor-pointer overflow-clip relative size-[24px]"
            onClick={handleClose}
          >
            <X size={24} className="text-black" />
          </button>
        </div>

        {/* Content */}
        <div className="flex flex-1 flex-col overflow-hidden lg:flex-row">
          {/* Left Side - Image Viewer */}
          <div className="relative h-[35vh] flex-none bg-gray-50 lg:h-auto lg:flex-1">
            {allImages.length > 0 && currentImageIndex < allImages.length ? (
              <>
                {/* Main Image */}
                <div className="absolute inset-4 flex items-center justify-center">
                  {allImages[currentImageIndex] &&
                    getImageSource(allImages[currentImageIndex]) && (
                      <img
                        key={`${currentImageIndex}-${
                          allImages[currentImageIndex]?.upload_picture ||
                          allImages[currentImageIndex]?.picture
                        }`}
                        src={getImageSource(allImages[currentImageIndex])}
                        alt={`${partData.part_name || "Part"} - View ${
                          currentImageIndex + 1
                        }`}
                        className="max-w-full max-h-full object-contain rounded-lg shadow-lg"
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                        onLoad={(e) => {
                          e.target.style.display = "block";
                        }}
                      />
                    )}
                </div>

                {/* Navigation Arrows */}
                {allImages.length > 1 && (
                  <>
                    <button
                      onClick={goToPreviousImage}
                      className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-80 hover:bg-opacity-100 rounded-full p-2 shadow-lg transition-all duration-200"
                    >
                      <ChevronLeft size={24} className="text-gray-700" />
                    </button>
                    <button
                      onClick={goToNextImage}
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-80 hover:bg-opacity-100 rounded-full p-2 shadow-lg transition-all duration-200"
                    >
                      <ChevronRight size={24} className="text-gray-700" />
                    </button>
                  </>
                )}

                {/* Image Counter */}
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-60 text-white px-3 py-1 rounded-full text-sm">
                  {currentImageIndex + 1} / {allImages.length}
                </div>
              </>
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-gray-500">
                <p>No images available</p>
              </div>
            )}
          </div>

          {/* Right Side - Part Details */}
          <div className="w-full shrink-0 border-t border-border-primary bg-background-secondary overflow-y-auto relative pb-24 lg:w-[500px] lg:border-l lg:border-t-0">
            {/* Header Section */}
            <div className="px-6 py-6 border-b border-border-primary">
              <h3
                className="text-2xl font-medium text-text-primary leading-normal"
                style={{
                  fontFamily: '"Noto Sans", sans-serif',
                  fontWeight: 500,
                  letterSpacing: "-0.24px",
                }}
              >
                Change the type of damage on vehicle
              </h3>
            </div>

            <div className="px-6 py-6 space-y-8 pb-20">
              {/* Damage Type Section */}
              <div>
                <label
                  className="block text-xs font-medium text-text-secondary mb-3 uppercase tracking-[-0.24px]"
                  style={{
                    fontFamily: '"Noto Sans", sans-serif',
                    fontWeight: 500,
                  }}
                >
                  DAMAGE TYPE
                </label>
                <div className="flex flex-wrap gap-2">
                  {[
                    { type: "Scratch", dotColor: "#f8bc05", isSelected: false },
                    { type: "Dent", dotColor: "#df3822", isSelected: true },
                    { type: "Broken", dotColor: "#df6a22", isSelected: false },
                  ].map((damage) => (
                    <div
                      key={damage.type}
                      className={`flex items-center gap-2 px-4 py-2 rounded-[32px] border ${
                        damage.isSelected
                          ? "border-[#1e2125] text-text-primary"
                          : "border-border-primary text-text-secondary"
                      }`}
                      style={{
                        fontFamily: '"Noto Sans", sans-serif',
                        fontWeight: 400,
                        fontSize: "16px",
                        letterSpacing: "-0.16px",
                      }}
                    >
                      <div
                        className="w-[10px] h-[10px] rounded-full"
                        style={{ backgroundColor: damage.dotColor }}
                      ></div>
                      {damage.type}
                    </div>
                  ))}
                </div>
              </div>

              {/* Suggested Action Section */}
              <div>
                <label
                  className="block text-xs font-medium text-text-secondary mb-3 uppercase tracking-[-0.12px]"
                  style={{
                    fontFamily: '"Noto Sans", sans-serif',
                    fontWeight: 500,
                  }}
                >
                  SUGGESTED ACTION
                </label>
                <div className="flex flex-wrap gap-2">
                  {["No Damage", "Repair", "Replace"].map((action) => (
                    <button
                      key={action}
                      onClick={() => handleActionChange(action)}
                      className={`px-4 py-2 rounded-[32px] border transition-all hover:shadow-sm ${
                        selectedAction === action
                          ? "border-[#1e2125] text-text-primary"
                          : "border-border-primary text-text-secondary hover:border-[#1e2125]"
                      }`}
                      style={{
                        fontFamily: '"Noto Sans", sans-serif',
                        fontWeight: 400,
                        fontSize: "16px",
                        letterSpacing: "-0.16px",
                      }}
                    >
                      {action}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Separator Line */}
            <div className="border-t border-border-primary mx-6 mb-6"></div>

            {/* Action Buttons - Fixed at bottom */}
            <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex gap-3 w-auto">
              <button
                onClick={handleClose}
                className="px-8 py-3 rounded-[32px] border-2 border-[#7e848c] text-text-primary font-semibold text-base tracking-[-0.16px] transition-all hover:bg-gray-50 whitespace-nowrap"
                style={{
                  fontFamily: '"Noto Sans", sans-serif',
                  fontWeight: 600,
                }}
              >
                CANCEL
              </button>
              <button
                onClick={handleSaveChanges}
                className={`px-8 py-3 rounded-[32px] border-2 font-semibold text-base tracking-[-0.16px] transition-all whitespace-nowrap ${
                  hasChanges
                    ? "border-[#1e2125] bg-text-primary text-white hover:bg-[#2a2a2a]"
                    : "border-[#7e848c] text-text-secondary hover:bg-gray-50 cursor-not-allowed opacity-60"
                }`}
                style={{
                  fontFamily: '"Noto Sans", sans-serif',
                  fontWeight: 600,
                }}
                disabled={!hasChanges}
              >
                SAVE CHANGES
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Add Comment Modal */}
      <AddCommentModal
        isOpen={showCommentModal}
        onClose={handleCommentModalClose}
        onSubmit={handleCommentSubmit}
        caseData={assessmentData}
        partName={partData?.part_name}
        selectedAction={selectedAction}
        partData={partData}
      />
    </div>
  );
};

export default EditPartModal;
